import { useMemo, useCallback, useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { NavigateOptions } from 'react-router-dom'
import { AppsRoutes } from '../getRoute'
import { routerSlice } from '../routerSlice'
import { setRouterPath } from '@wellesley/host-remote/api/actions'
import { useAppsNavigate } from '../useAppsNavigate'
import { staticState } from '@wellesley/host-remote/common/staticState'

const getQueryStringFromPath = (nextPath: string) => {
  const questionMarkIndex = nextPath.indexOf('?')
  if (questionMarkIndex === -1) return ''
  return nextPath.substring(questionMarkIndex + 1)
}

type URLSearchParamsInit =
  | string
  | [string, string][]
  | Record<string, string>
  | URLSearchParams

type SetURLSearchParams = (
  nextInit?:
    | URLSearchParamsInit
    | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOptions?: NavigateOptions,
) => void

export const useAppsSearchParams = (): [
  URLSearchParams,
  SetURLSearchParams,
] => {
  const previousPathRef = useRef('')

  const path = useSelector(routerSlice.selectors.selectPath)
  const navigate = useAppsNavigate()
  const dispatch = useDispatch()

  const getInitialSearch = () => {
    if (path) return getQueryStringFromPath(path)
    const { search } = staticState.get()
    return (window.location.search || search || '').replace(/^\?/, '')
  }

  const [localSearchParams, setLocalSearchParams] = useState<URLSearchParams>(
    () => {
      return new URLSearchParams(getInitialSearch())
    },
  )

  useEffect(() => {
    if (!path) return

    if (path === previousPathRef.current) return

    previousPathRef.current = path

    setLocalSearchParams(new URLSearchParams(getQueryStringFromPath(path)))
  }, [path])

  const searchParams = useMemo(() => {
    return new URLSearchParams(localSearchParams.toString())
  }, [localSearchParams])

  const setSearchParams: SetURLSearchParams = useCallback(
    (nextInit, navigateOptions) => {
      const newParams = new URLSearchParams()

      if (nextInit) {
        const nextParams =
          typeof nextInit === 'function' ? nextInit(searchParams) : nextInit

        if (typeof nextParams === 'string') {
          const params = new URLSearchParams(nextParams)
          for (const [key, value] of params.entries()) {
            newParams.set(key, value)
          }
        } else if (nextParams instanceof URLSearchParams) {
          for (const [key, value] of nextParams.entries()) {
            newParams.set(key, value)
          }
        } else if (Array.isArray(nextParams)) {
          for (const [key, value] of nextParams) {
            newParams.set(key, value)
          }
        } else {
          for (const [key, value] of Object.entries(nextParams)) {
            newParams.set(key, value)
          }
        }
      }

      setLocalSearchParams(newParams)

      const url = new URL(
        path || window.location.pathname,
        window.location.origin,
      )

      const queryString = newParams.toString()
      const newPath = url.pathname + (queryString ? `?${queryString}` : '')

      dispatch(setRouterPath(newPath))

      navigate(newPath as AppsRoutes, {
        replace: navigateOptions?.replace,
      })
    },
    [navigate, path, searchParams, dispatch],
  )

  return [searchParams, setSearchParams]
}
